import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Loader2, Search, Shield, Users, DollarSign, Target, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/errors";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <div className="border border-zinc-800 p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        {Icon && <Icon size={14} className="text-[#00D4FF]" strokeWidth={1.5} />}
      </div>
      <div className="font-display text-3xl">{value}</div>
      {hint && <div className="mono text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function StatsGrid({ stats }) {
  const u = stats.users || {};
  const r = stats.revenue || {};
  const t = stats.terminal || {};
  const l = stats.ledger || {};
  return (
    <div className="space-y-6" data-testid="admin-stats-grid">
      <div>
        <div className="overline mb-3 flex items-center gap-2">
          <Users size={10} strokeWidth={1.75} /> USERS
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="TOTAL" value={u.total ?? 0} />
          <StatCard label="ACTIVE PAID" value={u.active_paid ?? 0} hint="Stripe subscriptions" />
          <StatCard label="TRIALING" value={u.trialing ?? 0} hint="inside 3-day trial" />
          <StatCard label="FOUNDERS" value={u.founders ?? 0} hint="permanent grant" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard label="SIGNUPS 24H" value={u.signups_24h ?? 0} />
          <StatCard label="SIGNUPS 7D" value={u.signups_7d ?? 0} />
          <StatCard label="SIGNUPS 30D" value={u.signups_30d ?? 0} />
          <StatCard label="EXPIRED" value={u.expired ?? 0} hint="trial or sub lapsed" />
        </div>
      </div>

      <div>
        <div className="overline mb-3 flex items-center gap-2">
          <DollarSign size={10} strokeWidth={1.75} /> REVENUE
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="WEEKLY RR" value={`$${(r.wrr_usd ?? 0).toFixed(2)}`} icon={DollarSign} hint="active_paid × $9.99" />
          <StatCard label="MRR (EST)" value={`$${(r.mrr_est_usd ?? 0).toFixed(2)}`} hint="weekly × 52 / 12" />
          <StatCard label="PRICE" value={`$${(r.weekly_price_usd ?? 0).toFixed(2)}`} hint="/ week" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="overline mb-3 flex items-center gap-2">
            <Target size={10} strokeWidth={1.75} /> TERMINAL LOCKS
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatCard label="24H" value={t.locks_24h ?? 0} />
            <StatCard label="7D" value={t.locks_7d ?? 0} />
          </div>
        </div>
        <div>
          <div className="overline mb-3 flex items-center gap-2">
            <TrendingUp size={10} strokeWidth={1.75} /> LEDGER
          </div>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="GRADED" value={l.graded ?? 0} />
            <StatCard label="WINS" value={l.wins ?? 0} />
            <StatCard label="HIT %" value={`${(l.hit_rate_pct ?? 0).toFixed(1)}%`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function UserStatusBadge({ user }) {
  if (user.founder) {
    return <span className="mono text-[10px] px-1.5 py-0.5 border border-[#00D4FF] text-[#00D4FF]">FOUNDER</span>;
  }
  if (user.subscription_status === "active") {
    return <span className="mono text-[10px] px-1.5 py-0.5 border border-[#00D4FF]/50 text-[#00D4FF]/90">PAID</span>;
  }
  if (user.subscription_status === "trialing") {
    return <span className="mono text-[10px] px-1.5 py-0.5 border border-[#FFBF00]/60 text-[#FFBF00]">TRIAL</span>;
  }
  return <span className="mono text-[10px] px-1.5 py-0.5 border border-zinc-700 text-zinc-500">EXPIRED</span>;
}

function UserTable({ users, onExtend, onToggleFounder, meId }) {
  if (!users?.length) {
    return (
      <div className="border border-dashed border-zinc-800 p-10 text-center mono text-xs text-zinc-500">
        No users match.
      </div>
    );
  }
  return (
    <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-user-table">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">EMAIL</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">STATUS</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">JOINED</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">TRIAL ENDS</th>
            <th className="px-3 py-2 overline text-right bg-zinc-950 border-b border-zinc-800">REFS</th>
            <th className="px-3 py-2 overline text-right bg-zinc-950 border-b border-zinc-800">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-zinc-900/40" data-testid={`admin-user-row-${u.id}`}>
              <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-zinc-200">{u.email}</td>
              <td className="px-3 py-2.5 border-b border-zinc-900"><UserStatusBadge user={u} /></td>
              <td className="px-3 py-2.5 mono text-[11px] border-b border-zinc-900 text-zinc-400">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-3 py-2.5 mono text-[11px] border-b border-zinc-900 text-zinc-400">
                {u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-right text-zinc-300">
                {u.referral_count ?? 0}
              </td>
              <td className="px-3 py-2.5 border-b border-zinc-900 text-right space-x-2 whitespace-nowrap">
                <button
                  onClick={() => onExtend(u)}
                  data-testid={`admin-extend-${u.id}`}
                  className="mono text-[10px] px-2 py-1 border border-[#FFBF00]/60 text-[#FFBF00] hover:bg-[#FFBF00]/10"
                >
                  +7D TRIAL
                </button>
                <button
                  onClick={() => onToggleFounder(u)}
                  disabled={u.id === meId}
                  data-testid={`admin-toggle-founder-${u.id}`}
                  className="mono text-[10px] px-2 py-1 border border-[#00D4FF]/60 text-[#00D4FF] hover:bg-[#00D4FF]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={u.id === meId ? "Cannot toggle your own flag" : ""}
                >
                  {u.founder ? "UNFOUND" : "FOUND"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [query, setQuery] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not load stats"));
    }
  }, []);

  const loadUsers = useCallback(async (q) => {
    setLoadingUsers(true);
    try {
      const params = q ? { q, limit: 100 } : { limit: 50 };
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not load users"));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadUsers("");
  }, [loadStats, loadUsers]);

  const onExtend = useCallback(async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/extend-trial`, { days: 7 });
      toast.success(`Extended trial +7d for ${u.email}`);
      loadUsers(query);
      loadStats();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not extend trial"));
    }
  }, [loadUsers, loadStats, query]);

  const onToggleFounder = useCallback(async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-founder`);
      const nowFounder = data?.user?.founder;
      toast.success(`${u.email} · ${nowFounder ? "granted" : "revoked"} founder`);
      loadUsers(query);
      loadStats();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not toggle founder"));
    }
  }, [loadUsers, loadStats, query]);

  const onSearch = useCallback((e) => {
    e.preventDefault();
    loadUsers(query);
  }, [loadUsers, query]);

  // Hard gate: redirect non-founders out before even showing the page chrome.
  if (user && user.founder !== true) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div data-testid="admin-page">
      <Helmet>
        <title>Admin · Martingale Playbook</title>
      </Helmet>

      <div className="flex items-baseline justify-between border-b border-zinc-800 pb-4 mb-8">
        <div>
          <div className="overline neon-text mb-1 flex items-center gap-2">
            <Shield size={10} strokeWidth={1.75} />
            FOUNDER·ADMIN
          </div>
          <h1 className="font-display text-4xl lg:text-5xl leading-none">Mission control.</h1>
          <p className="mono text-[11px] text-zinc-500 mt-2">
            Founder-only view. Users, revenue, terminal activity, ledger health.
          </p>
        </div>
      </div>

      {stats ? (
        <StatsGrid stats={stats} />
      ) : (
        <div className="py-10 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Loading stats…
        </div>
      )}

      <div className="mt-12">
        <div className="flex items-center justify-between mb-4 gap-4">
          <h2 className="font-display text-2xl">Users</h2>
          <form onSubmit={onSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="search email"
                data-testid="admin-user-search"
                className="pl-8 pr-3 py-2 bg-zinc-950 border border-zinc-800 mono text-xs focus:border-[#00D4FF] focus:outline-none w-64"
              />
            </div>
            <button
              type="submit"
              className="mono text-[11px] px-3 py-2 border border-zinc-700 hover:border-[#00D4FF] hover:text-[#00D4FF]"
            >
              SEARCH
            </button>
          </form>
        </div>

        {loadingUsers ? (
          <div className="py-8 text-center mono text-xs text-zinc-500">
            <Loader2 size={14} className="inline animate-spin mr-2" />
            Loading users…
          </div>
        ) : (
          <UserTable
            users={users || []}
            onExtend={onExtend}
            onToggleFounder={onToggleFounder}
            meId={user?.id}
          />
        )}
      </div>
    </div>
  );
}
